import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';

// ant design icons
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import MinusOutlined from '@ant-design/icons/MinusOutlined';
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import TrophyOutlined from '@ant-design/icons/TrophyOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';

// third party
import { format, eachDayOfInterval, subDays } from 'date-fns';

// project imports
import MainCard from 'components/MainCard';
import { HistoryEmptyState } from 'components/HistoryPagination';
import useDatasheetHistory, { latestPerDay } from 'hooks/useDatasheetHistory';
import { CSVExport } from 'components/third-party/react-table';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';
// Controlling prompt levels + the per-target default helper. Shared with the
// Goals & Targets dialog and ColdProbeMode so the code list never drifts.
import { PROMPT_LEVELS, defaultPromptsForTarget } from 'utils/promptLevels';
import SpeechTextField from 'components/SpeechTextField';

const EMPTY_TARGETS = [];

// Session display: prefix a target's name with its domain code, colon-separated
// (e.g. "LR: Sit down"). Domain values arrive prefixed like "DOMAIN-LR", so strip
// that leading "DOMAIN-" and show just the code. Display-only — the saved targetName
// stays plain.
const targetLabel = (t) => {
  const name = (t && t.name) || '';
  const domain = ((t && t.domain) || '').replace(/^domain[-_\s]*/i, '').trim();
  return domain ? `${domain}: ${name}` : name;
};

// Shared styling for the SD (discriminative stimulus) tooltip — a clean, light
// "card" look with readable text instead of the default small dark tooltip.
const SD_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'grey.900',
      color: 'common.white',
      fontSize: '0.9rem',
      fontWeight: 500,
      lineHeight: 1.5,
      whiteSpace: 'pre-line',
      px: 1.5,
      py: 1,
      maxWidth: 340,
      borderRadius: 1.5,
      boxShadow: 3
    }
  },
  arrow: { sx: { color: 'grey.900' } }
};

const MultiTrialMode = ({
  viewMode = 'individual',
  onDataChange,
  targets = EMPTY_TARGETS,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  // Use configuration or defaults
  const activeTargets = targets;

  const [currentTarget, setCurrentTarget] = useState(0);
  const [currentTrial, setCurrentTrial] = useState(0);
  const [trialData, setTrialData] = useState(
    activeTargets.map((target) => ({
      targetId: target.id,
      trials: new Array(target.trials || 10).fill(null)
    }))
  );
  // Controlling-prompt state — a 2D grid shaped like trialData; each cell
  // promptLevels[targetIdx][trialIdx] is itself an ARRAY of prompt codes
  // (multi-select). Every trial is seeded with ALL of the target's Default
  // Controlling Prompts (set in Goals & Targets) so the therapist doesn't re-pick
  // them each trial; still overridable per trial. Targets with no defaults seed to
  // []. Each cell gets its OWN array (Array.from, not fill) so editing one trial
  // never mutates the others.
  const [promptLevels, setPromptLevels] = useState(
    activeTargets.map((target) => Array.from({ length: target.trials || 10 }, () => defaultPromptsForTarget(target)))
  );
  const [tabValue, setTabValue] = useState(0);
  /* Today's trials as they are scored — a LOCAL OVERLAY on the saved history
   * below, so the Historical tab reflects the session in progress. */
  const [historicalData, setHistoricalData] = useState({});

  /* The window the Historical grid draws. Declared here rather than with the other
   * view state below because the history fetch is bounded by it. */
  const [startDate, setStartDate] = useState(subDays(new Date(), 6)); // Last 7 days
  const [endDate, setEndDate] = useState(new Date());

  /* Previously submitted entries; without this the Historical tab only ever showed
   * the current browser session's work.
   *
   * Fetched BY DATE RANGE, not by page — the grid draws one column per day in the
   * window above, so a page of five arbitrary entries would leave columns empty on
   * days that do have data. */
  const { records: historyRecords, isEmpty: historyIsEmpty } = useDatasheetHistory(childId, 'multi_trial', {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd')
  });

  // day -> { [targetId]: { trials, promptLevels } }, latest entry per day winning.
  const savedHistory = useMemo(
    () =>
      latestPerDay(historyRecords, (record) => {
        const targets = record.raw_data?.targets;
        if (!Array.isArray(targets)) return undefined;
        const byTarget = {};
        targets.forEach((t) => {
          if (!t?.targetId) return;
          byTarget[t.targetId] = {
            trials: Array.isArray(t.trials) ? t.trials : [],
            promptLevels: Array.isArray(t.promptLevels) ? t.promptLevels : []
          };
        });
        return byTarget;
      }),
    [historyRecords]
  );

  // Unsubmitted local edits win over the saved copy for the same day and target.
  const mergedHistory = useMemo(() => {
    const out = { ...savedHistory };
    Object.entries(historicalData).forEach(([day, byTarget]) => {
      out[day] = { ...(out[day] || {}), ...byTarget };
    });
    return out;
  }, [savedHistory, historicalData]);

  const [expandedRows, setExpandedRows] = useState({});
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const initialRenderRef = useRef(true);

  // useState's initializer captures the first render's `activeTargets` only.
  // The parent loads targets asynchronously, so on mount they may be empty and
  // arrive later — re-sync trialData when the target list changes shape.
  useEffect(() => {
    setTrialData((prev) => {
      const sameShape =
        prev.length === activeTargets.length &&
        prev.every((td, i) => td.targetId === activeTargets[i]?.id && td.trials.length === (activeTargets[i]?.trials || 10));
      if (sameShape) return prev;
      return activeTargets.map((target) => ({
        targetId: target.id,
        trials: new Array(target.trials || 10).fill(null)
      }));
    });
    // Reset cursor if it falls outside the new target list.
    setCurrentTarget((idx) => (idx >= activeTargets.length ? 0 : idx));

    // Re-sync prompt 2D grid to match the new target list / trial counts,
    // re-seeding each trial with the target's Default Controlling Prompts (all).
    setPromptLevels((prev) => {
      const sameShape =
        prev.length === activeTargets.length &&
        prev.every((arr, i) => Array.isArray(arr) && arr.length === (activeTargets[i]?.trials || 10));
      if (sameShape) return prev;
      return activeTargets.map((target) => Array.from({ length: target.trials || 10 }, () => defaultPromptsForTarget(target)));
    });
  }, [activeTargets]);

  // Auto-save: whenever trialData or sessionNotes change, push form data to parent for IndexedDB persistence
  useEffect(() => {
    // Skip the initial render (no user data yet)
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    const totalCompleted = trialData.reduce((sum, td) => sum + td.trials.filter((t) => t !== null).length, 0);
    if (totalCompleted === 0) return; // Don't auto-save empty data

    const totalCorrect = trialData.reduce((sum, td) => sum + td.trials.filter((t) => t === 'correct').length, 0);
    const totalIncorrect = trialData.reduce((sum, td) => sum + td.trials.filter((t) => t === 'incorrect').length, 0);
    const accuracy = totalCompleted > 0 ? Math.round((totalCorrect / totalCompleted) * 100) : 0;

    const formData = {
      targets: activeTargets.map((target, index) => ({
        targetId: target.id,
        targetName: target.name,
        trials: trialData[index].trials,
        promptLevels: promptLevels[index] || [],
        correct: trialData[index].trials.filter((t) => t === 'correct').length,
        incorrect: trialData[index].trials.filter((t) => t === 'incorrect').length,
        completed: trialData[index].trials.filter((t) => t !== null).length
      })),
      sessionNotes,
      totalCorrect,
      totalIncorrect,
      totalCompleted,
      accuracy
    };

    if (onDataChange) onDataChange('multi-trial', formData);
  }, [trialData, promptLevels, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle save - uses offline-first pattern (always saves to IndexedDB, syncs in background)
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      // Calculate totals
      const totalCorrect = trialData.reduce((sum, td) => sum + td.trials.filter((t) => t === 'correct').length, 0);
      const totalIncorrect = trialData.reduce((sum, td) => sum + td.trials.filter((t) => t === 'incorrect').length, 0);
      const totalCompleted = trialData.reduce((sum, td) => sum + td.trials.filter((t) => t !== null).length, 0);
      const accuracy = totalCompleted > 0 ? Math.round((totalCorrect / totalCompleted) * 100) : 0;

      // Don't save if no data has been entered
      if (totalCompleted === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        targets: activeTargets.map((target, index) => ({
          targetId: target.id,
          targetName: target.name,
          trials: trialData[index].trials,
          promptLevels: promptLevels[index] || [],
          correct: trialData[index].trials.filter((t) => t === 'correct').length,
          incorrect: trialData[index].trials.filter((t) => t === 'incorrect').length,
          completed: trialData[index].trials.filter((t) => t !== null).length
        })),
        sessionNotes,
        totalCorrect,
        totalIncorrect,
        totalCompleted,
        accuracy
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('multi-trial', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? intl.formatMessage({ id: 'form.msg.dataSaved' }, { formName: intl.formatMessage({ id: 'form.multiTrial.title' }) })
            : intl.formatMessage({ id: 'form.msg.dataSavedOffline' }, { formName: intl.formatMessage({ id: 'form.multiTrial.title' }) }),
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.saveFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  // Get total completed trials
  const getTotalCompleted = () => {
    return trialData.reduce((sum, td) => sum + td.trials.filter((t) => t !== null).length, 0);
  };

  // First unanswered trial index for a given target (used when jumping to a
  // target so the cursor lands on the next thing the therapist needs to fill).
  const firstUnansweredTrial = (targetIdx, dataSource = trialData) => {
    const trials = dataSource[targetIdx]?.trials || [];
    const idx = trials.findIndex((t) => t === null);
    return idx === -1 ? 0 : idx;
  };

  // Within a target, advance trial → trial. At the end of a target, jump to
  // the next target's first unanswered trial. Auto-advance only fires when
  // BOTH the current trial's response AND the current trial's controlling
  // prompt are set. Prompts carry forward from the previous trial (see
  // buildPromptLevelsWithCarryForward), so usually only the response is needed.
  const maybeAdvanceTrial = (latestTrialData, promptForTrial) => {
    const responseForCurrent = latestTrialData[currentTarget]?.trials?.[currentTrial];
    const hasPrompt = Array.isArray(promptForTrial) ? promptForTrial.length > 0 : Boolean(promptForTrial);
    if (!responseForCurrent || !hasPrompt) return;

    const trialsForCurrentTarget = activeTargets[currentTarget]?.trials ?? 0;

    if (currentTrial < trialsForCurrentTarget - 1) {
      setTimeout(() => setCurrentTrial(currentTrial + 1), 300);
      return;
    }

    // Last trial of this target — move to the next target's first unanswered trial.
    if (currentTarget < activeTargets.length - 1) {
      setTimeout(() => {
        const nextTargetIdx = currentTarget + 1;
        setCurrentTarget(nextTargetIdx);
        setCurrentTrial(firstUnansweredTrial(nextTargetIdx, latestTrialData));
      }, 500);
    }
  };

  // Target navigation via Prev/Next — switches target and lands on that
  // target's first unanswered trial so re-entry feels resumable.
  const goToTarget = (nextTargetIdx) => {
    if (nextTargetIdx < 0 || nextTargetIdx >= activeTargets.length) return;
    setCurrentTarget(nextTargetIdx);
    setCurrentTrial(firstUnansweredTrial(nextTargetIdx));
  };

  const handleTrialResponse = (response) => {
    // Toggle: clicking the trial's current response clears it (deselect → null).
    const nextValue = trialData[currentTarget]?.trials?.[currentTrial] === response ? null : response;
    // Immutable update — replace the target's object AND its trials array.
    // Mutating in place (which the old code did) left stale references that
    // downstream cells (e.g. the Total column) could read between renders.
    const newTrialData = trialData.map((td, i) => {
      if (i !== currentTarget) return td;
      const newTrials = [...td.trials];
      newTrials[currentTrial] = nextValue;
      return { ...td, trials: newTrials };
    });
    setTrialData(newTrialData);

    // Update historical data with today's entry
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    setHistoricalData((prev) => {
      const updated = { ...prev };
      if (!updated[todayKey]) {
        updated[todayKey] = {};
      }
      if (!updated[todayKey][activeTargets[currentTarget].id]) {
        updated[todayKey][activeTargets[currentTarget].id] = {
          trials: new Array(activeTargets[currentTarget].trials).fill(null)
        };
      }
      updated[todayKey][activeTargets[currentTarget].id].trials[currentTrial] = nextValue;
      return updated;
    });

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)

    // Only advance on a real selection — never when deselecting.
    if (nextValue) maybeAdvanceTrial(newTrialData, promptLevels[currentTarget]?.[currentTrial]);
  };

  // Sets the prompt for one trial and CARRIES IT FORWARD to the following trials
  // of the same target, so a prompt picked on Trial 1 persists into Trial 2, 3…
  // until the therapist changes it. Carry-forward stops at the first later trial
  // that is already scored WITH a prompt — scored trials keep the prompt they were
  // run with. Scored trials that have no prompt yet are filled in and passed over.
  const buildPromptLevelsWithCarryForward = (targetIdx, trialIdx, codes) => {
    const next = [...promptLevels];
    const row = [...(next[targetIdx] || [])];
    row[trialIdx] = codes;
    const responses = trialData[targetIdx]?.trials || [];
    for (let i = trialIdx + 1; i < row.length; i += 1) {
      const scored = responses[i] !== null && responses[i] !== undefined;
      if (scored && (row[i] || []).length > 0) break;
      row[i] = [...codes];
    }
    next[targetIdx] = row;
    return next;
  };

  // Multi-select: `value` is the full array of selected prompt codes for the
  // current trial (from the non-exclusive ToggleButtonGroup).
  const handlePromptLevelChange = (value) => {
    const nextArray = Array.isArray(value) ? value : value ? [value] : [];
    const prevArray = Array.isArray(promptLevels[currentTarget]?.[currentTrial]) ? promptLevels[currentTarget][currentTrial] : [];
    setPromptLevels(buildPromptLevelsWithCarryForward(currentTarget, currentTrial, nextArray));
    // Advance only on the empty → non-empty transition, never on further toggles.
    if (prevArray.length === 0 && nextArray.length > 0) maybeAdvanceTrial(trialData, nextArray);
  };

  const handleGridResponse = (targetIndex, trialIndex, response) => {
    // Toggle: clicking the cell's current response clears it (deselect → null).
    const nextValue = trialData[targetIndex]?.trials?.[trialIndex] === response ? null : response;
    // Immutable update — replace the target's object AND its trials array so
    // the Total column (which derives correct/total from trialData[i].trials)
    // sees a new reference and recomputes on the next render.
    const newTrialData = trialData.map((td, i) => {
      if (i !== targetIndex) return td;
      const newTrials = [...td.trials];
      newTrials[trialIndex] = nextValue;
      return { ...td, trials: newTrials };
    });
    setTrialData(newTrialData);

    // Update historical data with today's entry
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    setHistoricalData((prev) => {
      const updated = { ...prev };
      if (!updated[todayKey]) {
        updated[todayKey] = {};
      }
      if (!updated[todayKey][activeTargets[targetIndex].id]) {
        updated[todayKey][activeTargets[targetIndex].id] = {
          trials: new Array(activeTargets[targetIndex].trials).fill(null)
        };
      }
      updated[todayKey][activeTargets[targetIndex].id].trials[trialIndex] = nextValue;
      return updated;
    });

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  // Get filtered date range for historical view
  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return [];
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [startDate, endDate]);

  // Check if a skill is mastered (3 consecutive correct responses across all trials for 3 days)
  const isSkillMastered = (targetId) => {
    const sortedDates = dateRange.map((date) => format(date, 'yyyy-MM-dd')).sort();
    let consecutiveDays = 0;

    for (const dateKey of sortedDates) {
      const targetData = mergedHistory[dateKey]?.[targetId];
      if (targetData && targetData.trials) {
        const correct = targetData.trials.filter((t) => t === 'correct').length;
        const total = targetData.trials.filter((t) => t !== null).length;
        // Consider a day mastered if 80% or more trials are correct
        if (total > 0 && correct / total >= 0.8) {
          consecutiveDays++;
          if (consecutiveDays >= 3) {
            return true;
          }
        } else if (total > 0) {
          consecutiveDays = 0;
        }
      }
    }
    return false;
  };

  // Render response icon
  const renderResponseIcon = (response) => {
    if (response === 'correct') {
      return <CheckOutlined style={{ color: theme.palette.success.main, fontSize: '1.2rem' }} />;
    } else if (response === 'incorrect') {
      return <CloseOutlined style={{ color: theme.palette.error.main, fontSize: '1.2rem' }} />;
    } else if (response === 'no-response') {
      return <MinusOutlined style={{ color: theme.palette.grey[400], fontSize: '1.2rem' }} />;
    }
    return '-';
  };

  // Export to CSV
  const exportToCSV = () => {
    const csvData = activeTargets.map((target) => {
      const row = { Target: target.name };
      dateRange.forEach((date) => {
        const dateKey = format(date, 'yyyy-MM-dd');
        const dateLabel = format(date, 'dd/MM');
        const targetData = mergedHistory[dateKey]?.[target.id];
        if (targetData && targetData.trials) {
          const correct = targetData.trials.filter((t) => t === 'correct').length;
          const total = targetData.trials.filter((t) => t !== null).length;
          row[dateLabel] = total > 0 ? `${correct}/${total}` : '-';
        } else {
          row[dateLabel] = '-';
        }
      });
      return row;
    });
    return csvData;
  };

  // Export to PDF (placeholder)
  const exportToPDF = () => {};

  // Toggle row expansion
  const toggleRowExpansion = (targetId, dateKey) => {
    const key = `${targetId}-${dateKey}`;
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Empty state — no targets configured
  if (activeTargets.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">{intl.formatMessage({ id: 'form.msg.noTargets' })}</Alert>
      </Box>
    );
  }

  // Individual View
  if (viewMode === 'individual') {
    const target = activeTargets[currentTarget];

    return (
      <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{intl.formatMessage({ id: 'form.msg.offlineNote' })}</strong>
            </Typography>
          </Alert>
        )}

        {/* Offline Mode Header */}
        {isOfflineMode && (
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                  {intl.formatMessage({ id: 'form.button.back' })}
                </Button>
                <Box>
                  <Typography variant="h5">
                    {intl.formatMessage({ id: 'form.multiTrial.title' })} - {childName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.label.offlineMode' })}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={handleOfflineSave}
                disabled={saving || getTotalCompleted() === 0}
              >
                {saving ? intl.formatMessage({ id: 'form.button.saving' }) : intl.formatMessage({ id: 'form.button.saveOffline' })}
              </Button>
            </Stack>
          </MainCard>
        )}

        <Stack spacing={3}>
          {/* Target Card */}
          <MainCard>
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              {/* Target Info — Prev / Next icon-only buttons flank the
                  "Target X of Y" counter so position is fixed regardless of
                  target name length. Each button hides when navigation in
                  that direction isn't possible. */}
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => goToTarget(currentTarget - 1)}
                  disabled={currentTarget === 0}
                  aria-label={intl.formatMessage({ id: 'form.button.previous', defaultMessage: 'Previous' })}
                  sx={{ visibility: currentTarget === 0 ? 'hidden' : 'visible', minWidth: 32, px: 0.75 }}
                >
                  <LeftOutlined />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: 'form.label.targetOfTotal' }, { current: currentTarget + 1, total: activeTargets.length })}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => goToTarget(currentTarget + 1)}
                  disabled={currentTarget >= activeTargets.length - 1}
                  aria-label={intl.formatMessage({ id: 'form.button.next', defaultMessage: 'Next' })}
                  sx={{ visibility: currentTarget >= activeTargets.length - 1 ? 'hidden' : 'visible', minWidth: 32, px: 0.75 }}
                >
                  <RightOutlined />
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <Typography variant="h3" textAlign="center" fontWeight={700}>
                  {targetLabel(target)}
                </Typography>
                {target.sdInstruction && (
                  <Tooltip title={target.sdInstruction} placement="top" arrow slotProps={SD_TOOLTIP_SLOT_PROPS}>
                    <InfoCircleOutlined style={{ fontSize: '1.6rem', color: theme.palette.primary.main, cursor: 'pointer' }} />
                  </Tooltip>
                )}
              </Stack>

              {/* Trial Selector — per-target trial cells. Each cell shows the
                  response icon (✓ / ✗ / –) once entered, or the trial number
                  while empty. Clicking a cell jumps to that trial within the
                  current target. */}
              <Stack spacing={1} alignItems="center" sx={{ width: '100%', maxWidth: 600, mt: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {intl.formatMessage({ id: 'form.multiTrial.selectTrial' })}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {Array.from({ length: target.trials }).map((_, trialIndex) => {
                    const response = trialData[currentTarget]?.trials?.[trialIndex];
                    const isCurrent = trialIndex === currentTrial;
                    const bgcolor =
                      response === 'correct'
                        ? 'success.lighter'
                        : response === 'incorrect'
                          ? 'error.lighter'
                          : response === 'no-response'
                            ? 'grey.200'
                            : 'grey.50';

                    return (
                      <Box
                        key={trialIndex}
                        onClick={() => setCurrentTrial(trialIndex)}
                        sx={{
                          width: 44,
                          height: 44,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 2,
                          borderColor: isCurrent ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          bgcolor,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)', boxShadow: 2 }
                        }}
                      >
                        {response ? (
                          renderResponseIcon(response)
                        ) : (
                          <Typography variant="caption" fontWeight={600} color="text.primary">
                            {trialIndex + 1}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Stack>

              {/* Trial Info - Prominent */}
              <Chip
                label={intl.formatMessage({ id: 'form.multiTrial.trialOfTotal' }, { current: currentTrial + 1, total: target.trials })}
                color="primary"
                sx={{
                  mt: 1,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  height: 32,
                  px: 1
                }}
              />

              {/* Controlling Prompt(s) — captured PER TRIAL, multi-select. Each
                  trial is pre-seeded with the target's Default Controlling Prompts
                  and is fully overridable (tick/untick any). Auto-advance to the
                  next trial requires the response AND ≥1 prompt selected. */}
              <Stack alignItems="center" spacing={1} sx={{ mt: 1, width: '100%', maxWidth: 600 }}>
                {(() => {
                  const trialResponseSet = !!trialData[currentTarget]?.trials?.[currentTrial];
                  const missingPrompt = (promptLevels[currentTarget]?.[currentTrial] || []).length === 0 && trialResponseSet;
                  return (
                    <Typography variant="caption" color={missingPrompt ? 'warning.main' : 'text.secondary'} fontWeight={600}>
                      {intl.formatMessage({ id: 'form.multiTrial.promptLevel', defaultMessage: 'Controlling Prompt' })}
                      {missingPrompt && ' • required to continue'}
                    </Typography>
                  );
                })()}
                <ToggleButtonGroup
                  value={promptLevels[currentTarget]?.[currentTrial] || []}
                  size="small"
                  onChange={(_, v) => handlePromptLevelChange(v)}
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  {PROMPT_LEVELS.map((p) => (
                    <ToggleButton
                      key={p.code}
                      value={p.code}
                      sx={{
                        px: 1.5,
                        fontWeight: 700,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'common.white',
                          '&:hover': { bgcolor: 'primary.dark' }
                        }
                      }}
                      title={p.label}
                    >
                      {p.code}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              {/* Response Buttons */}
              <Grid container spacing={2} sx={{ maxWidth: 600, mt: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    variant={trialData[currentTarget]?.trials?.[currentTrial] === 'correct' ? 'contained' : 'outlined'}
                    color="success"
                    fullWidth
                    size="medium"
                    onClick={() => handleTrialResponse('correct')}
                    sx={{ py: 2, fontSize: '0.95rem', fontWeight: 600 }}
                  >
                    <Stack spacing={0.5} alignItems="center">
                      <CheckOutlined style={{ fontSize: '1.5rem' }} />
                      <span>{intl.formatMessage({ id: 'common.correct' })}</span>
                    </Stack>
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    variant={trialData[currentTarget]?.trials?.[currentTrial] === 'incorrect' ? 'contained' : 'outlined'}
                    color="error"
                    fullWidth
                    size="medium"
                    onClick={() => handleTrialResponse('incorrect')}
                    sx={{ py: 2, fontSize: '0.95rem', fontWeight: 600 }}
                  >
                    <Stack spacing={0.5} alignItems="center">
                      <CloseOutlined style={{ fontSize: '1.5rem' }} />
                      <span>{intl.formatMessage({ id: 'common.incorrect' })}</span>
                    </Stack>
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    variant={trialData[currentTarget]?.trials?.[currentTrial] === 'no-response' ? 'contained' : 'outlined'}
                    color="secondary"
                    fullWidth
                    size="medium"
                    onClick={() => handleTrialResponse('no-response')}
                    sx={{ py: 2, fontSize: '0.95rem', fontWeight: 600 }}
                  >
                    <Stack spacing={0.5} alignItems="center">
                      <MinusOutlined style={{ fontSize: '1.5rem' }} />
                      <span>{intl.formatMessage({ id: 'common.notAvailable' })}</span>
                    </Stack>
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </MainCard>

          {/* Session Notes - Offline Mode Only */}
          {isOfflineMode && (
            <MainCard>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'form.label.sessionNotes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={3}
                placeholder={intl.formatMessage({ id: 'form.label.notesPlaceholder' })}
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
            </MainCard>
          )}
        </Stack>
      </Box>
    );
  }

  // Grid View
  return (
    <MainCard>
      {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
      {!isOfflineMode && !isOnline && (
        <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Working offline</strong> - Your data is being auto-saved locally and will sync automatically when connection is
            restored.
          </Typography>
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
        <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
      </Tabs>

      <Box sx={{ pt: 3 }}>
        {/* Tab 0: Data Entry */}
        {tabValue === 0 && (
          <>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  '& .MuiTableCell-root': { py: 1.25 }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>#</TableCell>
                    <TableCell width={280}>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                    {Array.from({ length: 10 }, (_, i) => (
                      <TableCell key={i} align="center" sx={{ width: 76, minWidth: 76, px: 0.5 }}>
                        T{i + 1}
                      </TableCell>
                    ))}
                    <TableCell align="center" width={100} sx={{ fontWeight: 600, bgcolor: 'primary.lighter' }}>
                      {intl.formatMessage({ id: 'form.multiTrial.total' })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTargets.map((target, targetIndex) => {
                    // trialData may briefly lag activeTargets between render and sync useEffect.
                    const trials = trialData[targetIndex]?.trials || [];
                    const correct = trials.filter((t) => t === 'correct').length;
                    const total = trials.filter((t) => t !== null).length;
                    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;

                    return (
                      <TableRow key={target.id} hover>
                        <TableCell>{targetIndex + 1}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {targetLabel(target)}
                            </Typography>
                            {target.sdInstruction && (
                              <Tooltip title={target.sdInstruction} placement="top" arrow slotProps={SD_TOOLTIP_SLOT_PROPS}>
                                <InfoCircleOutlined style={{ fontSize: '1.3rem', color: theme.palette.primary.main, cursor: 'pointer' }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        {trials.map((trial, trialIndex) => (
                          <TableCell key={trialIndex} align="center" sx={{ px: 0.5 }}>
                            <Stack spacing={0.5} alignItems="center">
                              <ToggleButtonGroup
                                value={trial}
                                exclusive
                                onChange={(e, value) => value && handleGridResponse(targetIndex, trialIndex, value)}
                                size="small"
                                orientation="vertical"
                              >
                                {/* Heavier selected styling: filled background +
                                    white icon so the chosen trial is unmistakable
                                    at a glance (the default subtle tint was too
                                    close to the unselected state). */}
                                <ToggleButton
                                  value="correct"
                                  color="success"
                                  sx={{
                                    width: 40,
                                    height: 28,
                                    p: 0,
                                    '&.Mui-selected': {
                                      bgcolor: 'success.main',
                                      color: 'common.white',
                                      '&:hover': { bgcolor: 'success.dark' }
                                    }
                                  }}
                                >
                                  <CheckOutlined style={{ fontSize: '1rem' }} />
                                </ToggleButton>
                                <ToggleButton
                                  value="incorrect"
                                  color="error"
                                  sx={{
                                    width: 40,
                                    height: 28,
                                    p: 0,
                                    '&.Mui-selected': {
                                      bgcolor: 'error.main',
                                      color: 'common.white',
                                      '&:hover': { bgcolor: 'error.dark' }
                                    }
                                  }}
                                >
                                  <CloseOutlined style={{ fontSize: '1rem' }} />
                                </ToggleButton>
                                <ToggleButton
                                  value="no-response"
                                  color="standard"
                                  sx={{
                                    width: 40,
                                    height: 28,
                                    p: 0,
                                    '&.Mui-selected': {
                                      bgcolor: 'grey.600',
                                      color: 'common.white',
                                      '&:hover': { bgcolor: 'grey.700' }
                                    }
                                  }}
                                >
                                  <MinusOutlined style={{ fontSize: '1rem' }} />
                                </ToggleButton>
                              </ToggleButtonGroup>
                              {/* Per-trial controlling-prompt dropdown — compact,
                                  label-less, MULTI-select. Writes an array of codes
                                  into promptLevels[target][trial]; shows the picked
                                  codes joined (e.g. "PVP,IND") or "—" when empty. */}
                              <Select
                                size="small"
                                multiple
                                displayEmpty
                                value={Array.isArray(promptLevels[targetIndex]?.[trialIndex]) ? promptLevels[targetIndex][trialIndex] : []}
                                onChange={(e) => {
                                  const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                                  setPromptLevels(buildPromptLevelsWithCarryForward(targetIndex, trialIndex, val));
                                }}
                                renderValue={(selected) => ((selected || []).length ? selected.join(',') : <em>—</em>)}
                                sx={{
                                  width: 64,
                                  fontSize: '0.75rem',
                                  '& .MuiSelect-select': { py: 0.75, px: 0.5, fontSize: '0.75rem' }
                                }}
                              >
                                {PROMPT_LEVELS.map((p) => (
                                  <MenuItem key={p.code} value={p.code} title={p.label} sx={{ fontSize: '0.75rem' }}>
                                    <Checkbox
                                      size="small"
                                      checked={(promptLevels[targetIndex]?.[trialIndex] || []).includes(p.code)}
                                      sx={{ p: 0.25 }}
                                    />
                                    {p.code}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ bgcolor: 'primary.lighter' }}>
                          <Stack spacing={0.5} alignItems="center">
                            <Typography variant="body2" fontWeight={700}>
                              {correct}/{total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {percentage}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Tab 1: Historical Data */}
        {tabValue === 1 && (
          <>
            {/* Date Filters and Export */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={intl.formatMessage({ id: 'form.label.startDate' })}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 }
                    }
                  }}
                />
                <DatePicker
                  label={intl.formatMessage({ id: 'form.label.endDate' })}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 }
                    }
                  }}
                />
              </LocalizationProvider>

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={1}>
                <CSVExport data={exportToCSV()} filename={`multi-trial-history-${format(new Date(), 'yyyy-MM-dd')}.csv`} />
                <Button variant="outlined" size="small" startIcon={<FilePdfOutlined />} onClick={exportToPDF}>
                  {intl.formatMessage({ id: 'form.button.exportPdf' })}
                </Button>
              </Stack>
            </Stack>

            {/* Historical Data Grid. Nothing saved in the picked window and nothing
                entered today — a grid of dashes says less than saying so. */}
            {historyIsEmpty && Object.keys(historicalData).length === 0 ? (
              <HistoryEmptyState />
            ) : (
              <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          bgcolor: 'background.paper',
                          zIndex: 3,
                          minWidth: 200,
                          fontWeight: 600,
                          borderRight: 2,
                          borderColor: 'divider'
                        }}
                      >
                        {intl.formatMessage({ id: 'form.label.target' })}
                      </TableCell>
                      {dateRange.map((date) => (
                        <TableCell
                          key={format(date, 'yyyy-MM-dd')}
                          align="center"
                          sx={{
                            minWidth: 100,
                            fontWeight: 600,
                            bgcolor:
                              format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? 'primary.lighter' : 'background.paper'
                          }}
                        >
                          {format(date, 'dd/MM')}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTargets.map((target) => {
                      const isMastered = isSkillMastered(target.id);
                      return (
                        <>
                          <TableRow key={target.id} hover>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 0,
                                bgcolor: 'background.paper',
                                zIndex: 2,
                                fontWeight: 600,
                                borderRight: 2,
                                borderColor: 'divider'
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title={target.description} placement="right">
                                  <Typography variant="body2" fontWeight={600}>
                                    {targetLabel(target)}
                                  </Typography>
                                </Tooltip>
                                {isMastered && (
                                  <Tooltip title={intl.formatMessage({ id: 'form.multiTrial.masteredTooltip' })}>
                                    <Chip
                                      icon={<TrophyOutlined style={{ fontSize: '0.9rem' }} />}
                                      label={intl.formatMessage({ id: 'form.label.mastered' })}
                                      size="small"
                                      color="warning"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                            {dateRange.map((date) => {
                              const dateKey = format(date, 'yyyy-MM-dd');
                              const targetData = mergedHistory[dateKey]?.[target.id];
                              const correct = targetData?.trials?.filter((t) => t === 'correct').length || 0;
                              const total = targetData?.trials?.filter((t) => t !== null).length || 0;
                              const expandKey = `${target.id}-${dateKey}`;
                              const isExpanded = expandedRows[expandKey];

                              return (
                                <TableCell
                                  key={dateKey}
                                  align="center"
                                  sx={{
                                    bgcolor:
                                      format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                                        ? 'primary.lighter'
                                        : 'background.paper',
                                    cursor: total > 0 ? 'pointer' : 'default'
                                  }}
                                  onClick={() => total > 0 && toggleRowExpansion(target.id, dateKey)}
                                >
                                  {total > 0 ? (
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                      <Typography variant="body2" fontWeight={600}>
                                        {correct}/{total}
                                      </Typography>
                                      <IconButton size="small" sx={{ p: 0 }}>
                                        {isExpanded ? (
                                          <UpOutlined style={{ fontSize: '0.8rem' }} />
                                        ) : (
                                          <DownOutlined style={{ fontSize: '0.8rem' }} />
                                        )}
                                      </IconButton>
                                    </Stack>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          {/* Drill-down row for trial details */}
                          {dateRange.map((date) => {
                            const dateKey = format(date, 'yyyy-MM-dd');
                            const expandKey = `${target.id}-${dateKey}`;
                            const isExpanded = expandedRows[expandKey];
                            const targetData = mergedHistory[dateKey]?.[target.id];

                            if (!isExpanded || !targetData) return null;

                            return (
                              <TableRow key={`${target.id}-${dateKey}-detail`}>
                                <TableCell colSpan={dateRange.length + 1} sx={{ py: 0, bgcolor: 'grey.50' }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ py: 2, px: 3 }}>
                                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        {targetLabel(target)} - {format(date, 'MMMM dd, yyyy')} -{' '}
                                        {intl.formatMessage({ id: 'form.multiTrial.trialDetails' })}
                                      </Typography>
                                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        {targetData.trials.map((trial, idx) => (
                                          <Paper
                                            key={idx}
                                            sx={{
                                              p: 1,
                                              minWidth: 60,
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'center',
                                              bgcolor:
                                                trial === 'correct'
                                                  ? 'success.lighter'
                                                  : trial === 'incorrect'
                                                    ? 'error.lighter'
                                                    : trial === 'no-response'
                                                      ? 'grey.200'
                                                      : 'background.paper'
                                            }}
                                          >
                                            <Typography variant="caption" color="text.secondary">
                                              T{idx + 1}
                                            </Typography>
                                            {renderResponseIcon(trial)}
                                          </Paper>
                                        ))}
                                      </Stack>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Summary Stats */}
            <Stack direction="row" spacing={3} sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckOutlined style={{ color: theme.palette.success.main }} />
                <Typography variant="body2">{intl.formatMessage({ id: 'common.correct' })}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CloseOutlined style={{ color: theme.palette.error.main }} />
                <Typography variant="body2">{intl.formatMessage({ id: 'common.incorrect' })}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <MinusOutlined style={{ color: theme.palette.grey[400] }} />
                <Typography variant="body2">{intl.formatMessage({ id: 'common.notAvailable' })}</Typography>
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.multiTrial.clickCellDetails' })}
              </Typography>
            </Stack>
          </>
        )}
      </Box>
    </MainCard>
  );
};

MultiTrialMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      trials: PropTypes.number
    })
  ),
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func
};

export default MultiTrialMode;
